import { RackSavantClient, type StoredTokens, type TokenStore } from '@racksavant/core';

const TOKEN_KEY = 'racksavant.tokens.v1';

class LocalStorageTokenStore implements TokenStore {
  private cache: StoredTokens | null | undefined;

  get(): StoredTokens | null {
    if (this.cache !== undefined) return this.cache;
    this.cache = read();
    return this.cache;
  }

  set(tokens: StoredTokens | null): void {
    this.cache = tokens;
    try {
      if (tokens) window.localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
      else window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Private mode / disabled storage: the session simply lives in memory.
    }
  }
}

function read(): StoredTokens | null {
  try {
    const raw = window.localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (!parsed?.accessToken || !parsed?.refreshToken) return null;
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0,
    };
  } catch {
    return null;
  }
}

export const tokenStore = new LocalStorageTokenStore();

/**
 * The auth store registers itself here rather than being imported directly,
 * so the client stays free of any store dependency (and of a cycle).
 */
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/** Set by <App/> so a 401 can bounce us to /auth without a full page reload. */
type Navigator = (to: string) => void;
let navigator: Navigator | null = null;

export function setAppNavigator(fn: Navigator | null): void {
  navigator = fn;
}

export function navigateTo(to: string): void {
  if (navigator) navigator(to);
  else if (typeof window !== 'undefined') window.location.assign(to);
}

export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8787').replace(/\/+$/, '');

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

export const client = new RackSavantClient({
  baseUrl: API_URL,
  tokenStore,
  onUnauthorized: () => {
    unauthorizedHandler?.();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
      navigateTo('/auth');
    }
  },
});

/** Absolute URL for a media key or an already-absolute URL from the API. */
export function mediaUrl(value: string | null | undefined): string {
  if (!value) return '';
  if (/^(https?:|data:|blob:)/.test(value)) return value;
  return `${API_URL}${value.startsWith('/') ? '' : '/'}${value}`;
}
